import { useEffect, useState } from "react";
import { ArrowLeft, ChevronDown, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import BottomNavbar from "../components/BottomNavbar";
import { getRequest, putRequest } from "../helpers/api.helper";

type Caste = { id: number | string; caste: string };

export default function CastePreferences() {
  const [castes, setCastes] = useState<Caste[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [religions, setReligions] = useState<string[]>([]);
  const [selectedReligions, setSelectedReligions] = useState<string[]>([]);
  const [savedReligions, setSavedReligions] = useState<string[]>([]);
  const [religionOpen, setReligionOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await getRequest("/bureau/caste-preferences");
        if (cancelled) return;
        if (!response.success || !response.data) throw new Error(response.message || "Unable to load caste preferences.");
        setCastes(response.data.castes);
        setSelected(response.data.selectedCastes);
        setSaved(response.data.selectedCastes);
        setReligions(response.data.religions);
        setSelectedReligions(response.data.selectedReligions);
        setSavedReligions(response.data.selectedReligions);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load caste preferences.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, []);

  const toggle = (name: string) => setSelected((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  const toggleReligion = (name: string) => setSelectedReligions((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  const religionLabel = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);
  const dirty = selected.length !== saved.length || selected.some((name) => !saved.includes(name)) ||
    selectedReligions.length !== savedReligions.length || selectedReligions.some((name) => !savedReligions.includes(name));
  const filtered = castes.filter(({ caste }) => caste.toLowerCase().includes(search.trim().toLowerCase()));

  const save = async () => {
    setSaving(true);
    try {
      const response = await putRequest("/bureau/caste-preferences", { selectedCastes: selected, selectedReligions });
      if (!response.success || !response.data) throw new Error(response.message || "Unable to save caste preferences.");
      setSelected(response.data.selectedCastes);
      setSaved(response.data.selectedCastes);
      setSelectedReligions(response.data.selectedReligions);
      setSavedReligions(response.data.selectedReligions);
      setReligionOpen(false);
      setOpen(false);
      toast.success(response.message || "Caste preferences updated.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Unable to save caste preferences.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-purple-50 pb-28 text-gray-900">
      <header className="flex items-center gap-3 bg-gradient-to-r from-purple-600 to-fuchsia-500 px-5 py-5 text-white">
        <Link to="/admin/dashboard" aria-label="Back to dashboard" className="rounded-full p-2 hover:bg-white/20"><ArrowLeft size={24} /></Link>
        <h1 className="text-xl font-semibold">Edit Caste Preference</h1>
      </header>
      <main className="mx-auto max-w-2xl p-4 sm:p-6">
        <section className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm sm:p-7" aria-busy={loading || saving}>
          <h2 className="text-lg font-semibold">Religions &amp; castes displayed on your website &amp; app</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Select the religions and castes whose profiles should appear on your Share Website and White-Label User App. Save your changes to update both.</p>
          {loading ? <p className="mt-6" role="status">Loading caste preferences…</p> : error ? <p className="mt-6 text-red-600" role="alert">{error} Reopen this page to try again.</p> : (
            <>
              <label className="mb-2 mt-6 block font-medium" id="religion-selector-label">Select Religion</label>
              <button type="button" disabled={saving} onClick={() => { setReligionOpen(!religionOpen); setOpen(false); }} aria-expanded={religionOpen} aria-controls="religion-options" aria-labelledby="religion-selector-label religion-selector-value" className="flex w-full items-center justify-between rounded-xl border border-purple-200 px-4 py-3 text-left focus:ring-2 focus:ring-purple-500">
                <span id="religion-selector-value">{selectedReligions.length ? selectedReligions.map(religionLabel).join(", ") : "All Religions"}</span><ChevronDown size={20} className="shrink-0" />
              </button>
              {religionOpen && (
                <div id="religion-options" className="mt-2 rounded-xl border border-purple-200 bg-white p-3">
                  {religions.map((name) => (
                    <label key={name} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-3 hover:bg-purple-50">
                      <input type="checkbox" checked={selectedReligions.includes(name)} disabled={saving} onChange={() => toggleReligion(name)} className="h-4 w-4 accent-purple-600" />
                      <span>{religionLabel(name)}</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedReligions.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedReligions.map((name) => <button type="button" key={name} disabled={saving} onClick={() => toggleReligion(name)} aria-label={`Remove ${religionLabel(name)}`} className="flex items-center gap-2 rounded-full bg-purple-100 px-3 py-2 text-sm text-purple-800">{religionLabel(name)}<X size={14} /></button>)}
                </div>
              )}
              <label className="mb-2 mt-6 block font-medium" id="caste-selector-label">Select Caste</label>
              <button type="button" disabled={saving} onClick={() => { setOpen(!open); setReligionOpen(false); }} aria-expanded={open} aria-controls="caste-options" aria-labelledby="caste-selector-label caste-selector-value" className="flex w-full items-center justify-between rounded-xl border border-purple-200 px-4 py-3 text-left focus:ring-2 focus:ring-purple-500">
                <span id="caste-selector-value">{selected.length ? `${selected.length} castes selected` : "All Castes"}</span><ChevronDown size={20} />
              </button>
              {open && (
                <div id="caste-options" className="mt-2 rounded-xl border border-purple-200 bg-white p-3">
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-3">
                    <Search size={18} className="text-gray-400" />
                    <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search castes…" aria-label="Search castes" className="min-w-0 flex-1 bg-transparent py-3 outline-none" />
                  </div>
                  <div className="mt-2 max-h-64 overflow-y-auto">
                    {filtered.map(({ id, caste }) => (
                      <label key={id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-3 hover:bg-purple-50">
                        <input type="checkbox" checked={selected.includes(caste)} disabled={saving} onChange={() => toggle(caste)} className="h-4 w-4 accent-purple-600" />
                        <span>{caste}</span>
                      </label>
                    ))}
                    {!filtered.length && <p className="px-2 py-4 text-sm text-gray-500">No castes found.</p>}
                  </div>
                </div>
              )}
              {selected.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selected.map((name) => <button type="button" key={name} disabled={saving} onClick={() => toggle(name)} aria-label={`Remove ${name}`} className="flex items-center gap-2 rounded-full bg-purple-100 px-3 py-2 text-sm text-purple-800">{name}<X size={14} /></button>)}
                </div>
              )}
              <p className="mt-4 text-sm text-gray-500">No religion selection means All Religions. No caste selection means All Castes. Existing profile privacy and visibility rules still apply.</p>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <button type="button" disabled={saving || (!selected.length && !selectedReligions.length)} onClick={() => { setSelected([]); setSelectedReligions([]); }} className="px-2 py-2 text-sm text-purple-700 disabled:opacity-40">Clear selection</button>
                <button type="button" disabled={saving || !dirty} onClick={save} className="rounded-xl bg-purple-600 px-6 py-3 font-medium text-white hover:bg-purple-700 disabled:opacity-50">{saving ? "Saving…" : "Save / Update"}</button>
              </div>
            </>
          )}
        </section>
      </main>
      <BottomNavbar />
    </div>
  );
}
